import * as THREE from 'three';
import { type ShipSpec, type ModuleSpec, type ModuleKind, type Livery, type HullManifest, type ManifestAttach, resolveSpec } from './shipSpec';
import { applyTriplanar } from './triplanar';
import { instancePart, PART_SLOTS, type PartMats, type PartName } from './parts';
import { bannerTexture, lineTexture, hazardStripeTexture, hazardRingTexture, curvedDecal, curvedDecalGeometry, decalMaterial as makeDecalMaterial } from './decals';
import { createHeatState, heatFrac, type HeatState } from './heat';
import { createNavLights, type NavLights } from './navLights';
import { applyRadiatorGlow, loopTemperature, stepPanelTemperature, RADIATOR, type RadiatorGlow } from './radiatorGlow';
import { createHullDamage, type HullDamage } from './hullDamage';
import { createDriveHeat, type DriveHeat, type DriveHeatRead } from './driveHeat';

// Custos-class patrol frigate — parametric, code-owned, SPEC-DRIVEN builder.
// Canon: phase-h/08-vessels/01-custos-class-frigate.md.
//
// Signature POSTURE TELL: radiators DEPLOYED + cherry-red = cruise; STOWED flat
// + dark = combat. ONE ship; one `deployFraction ∈ [0,1]` drives the hinge
// angle and the silhouette beam in lockstep, and puts the panels on or off the
// coolant loop - the glow follows the panels' own temperature
// (scene/radiatorGlow.ts), which lags the posture as a stowed wing cools.
//
// Architecture (per docs/SHIP-ASSET-PIPELINE.md + PR #8 review):
//  - Geometry is composed by walking a ShipSpec's `modules` list — each module
//    is a NAMED group (so it can be hidden/damaged/swizzled), positions live in
//    DATA (shipSpec.ts), not inline conditionals.
//  - Textures are loaded ONCE into a module-level cache and shared across all
//    ships (variant-independent); only the pennant canvas texture is per-ship.
//  - createCustos returns a dispose() that frees per-instance geometries +
//    materials (shared cached textures are intentionally NOT disposed).
//
// Convention: forward = -Z.

// ---- the posture tuning surface (the signature lives here) ----
const POSTURE = {
  deployAng: 0.0,                                   // radiators fanned wide (cruise)
  stowAng: THREE.MathUtils.degToRad(82),            // radiators swung up, hugging spine (combat)
  // an authored vane swings down instead, near plumb beside the keel; one with
  // a fold line folds its outer panel in first. Both phases are shares of the stow.
  vaneStowAng: THREE.MathUtils.degToRad(86),
  vaneFold: [0, 0.55] as const,
  vaneSwing: [0.45, 1] as const,
  travelSeconds: 1.6,                               // radiators are slow, mechanical
};

export interface CustosOptions {
  scale?: number;
  initialDeploy?: number;                 // 0 = stowed, 1 = deployed (applied immediately)
  variant?: string | ShipSpec;            // 'pf' | 'pfg' | 'pfe' | 'pfh' or a ShipSpec
  pennant?: string;                       // hull number; defaults to spec.pennantDefault
  sigil?: boolean;                        // override spec.sigil
  /** false keeps an authored drive's throat lit and its bells cold whatever it burns:
   *  the plume dojo's `?driveheat=0`, the control drive-transient-check.mjs must fail. */
  driveHeat?: boolean;
  /** Service weathering, 0 clean, 1 (the default) a working ship and past it:
   *  chipped edges, grime, and on an authored drive heat tint and soot. */
  grit?: number;
}

export type ModuleDamageState = 'green' | 'yellow' | 'red' | 'destroyed';
export interface ModuleHit { id: string; kind: ModuleKind; state: ModuleDamageState }

export interface Custos {
  group: THREE.Group;       // root — moved/rotated by physics
  visual: THREE.Group;      // child — internal idle motion (drop-in compatible with scene/ship.ts Ship)
  setDeployTarget: (f: number) => void;
  /** Put the radiator posture back where a save found it: the live fraction as well as the target, with the rig drawn at it. */
  restoreDeploy: (fraction: number, target: number) => void;
  toggleDeploy: () => void;
  setThrottle: (t: number) => void;
  state: { deployFraction: number; deployTarget: number; throttle: number; beam: number; variantId: string; variantName: string };
  /** The hull's own heat budget, sized off `spec.radiatorPanelsPerSide`. The
   *  sim tick steps it (captain registry for an NPC, src/main.ts for the
   *  player); `update` below only draws. */
  heat: HeatState;
  /** What the radiators are doing thermally, drawn from `heat` and never written back. */
  radiator: { readonly panelK: number; readonly loopK: number };
  /** An authored drive's throat and bell heat, drawn off `state.throttle`; absent on a procedural hull. */
  driveHeat?: { read: () => DriveHeatRead };
  update: (dt: number, t: number) => void;
  dispose: () => void;      // frees per-instance geometries + materials (NOT shared textures)
  /** Module damage model — one roll wounds the 3D group AND the SSD cell. */
  damage: {
    states: () => ModuleHit[];
    /** Localize a world-space hit to the nearest spine module and degrade it one step. */
    damageAt: (worldHit: THREE.Vector3) => ModuleHit | null;
    setState: (id: string, state: ModuleDamageState) => ModuleHit | null;
    reset: () => void;
    /** Every module to a saved state in one pass, the mesh, the SSD cells and
     *  the hull with it - but `onChange` never fires: a restore is not a hit
     *  landing, and must not log one. */
    restoreStates: (states: readonly { id: string; state: ModuleDamageState }[]) => void;
    /** Drive throughput 1 → 0.05 as the drive module degrades. */
    driveFactor: () => number;
    onChange: (cb: (hit: ModuleHit) => void) => void;
  };
}

// ---- shared texture cache (load once, share across every ship) ----
THREE.Cache.enabled = true;
const _texLoader = new THREE.TextureLoader();
const _setCache = new Map<string, { albedo: THREE.Texture; normal: THREE.Texture; rough: THREE.Texture }>();
function texSet(base: string) {
  const hit = _setCache.get(base);
  if (hit) return hit;
  const albedo = _texLoader.load(`/art/ships/${base}_albedo.png`);
  const normal = _texLoader.load(`/art/ships/${base}_normal.png`);
  const rough = _texLoader.load(`/art/ships/${base}_rough.png`);
  albedo.colorSpace = THREE.SRGBColorSpace;
  normal.colorSpace = THREE.NoColorSpace;
  rough.colorSpace = THREE.NoColorSpace;
  for (const t of [albedo, normal, rough]) {
    // triplanar samples at arbitrary object-space coords — wrap, no repeat tuning
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.anisotropy = 8;
  }
  const set = { albedo, normal, rough };
  _setCache.set(base, set);
  return set;
}
const _sigilCache = { tex: null as THREE.Texture | null };
function sigilTexture(): THREE.Texture {
  if (!_sigilCache.tex) {
    const t = _texLoader.load('/art/ships/sigil-lance.png');
    t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8;
    _sigilCache.tex = t;
  }
  return _sigilCache.tex;
}

// Code-drawn pennant decal: text is data, painted to a canvas (crisp, per-ship),
// NOT diffused (diffusion makes gibberish). Transparent stencil. Per-instance.
function makePennantTexture(text: string, color = '#dfe6f0'): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 256; c.height = 128;
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, 256, 128);
  ctx.fillStyle = color;
  ctx.font = 'bold 84px "Arial Narrow", Arial, sans-serif';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(0,0,0,0.5)'; ctx.shadowBlur = 4;
  ctx.fillText(text, 128, 68);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 8;
  return tex;
}

// ---- per-instance material set (livery tint differs per variant; cheap) ----
interface MatSet {
  hull: THREE.MeshStandardMaterial; slate: THREE.MeshStandardMaterial;
  bandA: THREE.MeshStandardMaterial; bandB: THREE.MeshStandardMaterial;
  armor: THREE.MeshStandardMaterial; rad: THREE.MeshStandardMaterial;
  plume: THREE.MeshBasicMaterial; port: THREE.MeshStandardMaterial;
  radGlow?: RadiatorGlow;
}
// A radar-absorbent coating is paint with a little metal in it. Bare polished
// gold caught the sun and bloomed into a haze over the whole stern.
const COATED_HULL = { roughness: 1.9, metalness: 0.35 };   // roughness times reg-hull's map, about 0.74
const COATED_TRIM = { roughness: 0.55, metalness: 0.6 };
const COATED_STRAKE = { roughness: 0.6, metalness: 0.45 };

function createMaterials(liv: Livery, w: number): MatSet {
  const hullT = texSet('hull'), greebleT = texSet('greeble');
  const radT = texSet('radiator'), armorT = texSet('bowarmor');
  const coated = liv.finish === 'coated';
  // the shared hull albedo carries baked hatches and red and blue stripes; a
  // coating takes the clean register plate's seams and its roughness, not its colour
  const plateT = coated ? texSet('reg-hull') : hullT;
  const mats: MatSet = {
    // a weathered coating shows the plate's mismatched panels through it
    hull: coated
      ? new THREE.MeshStandardMaterial({ color: liv.hull, ...COATED_HULL, map: w > 0 ? plateT.albedo : null,
        normalMap: plateT.normal, roughnessMap: plateT.rough, normalScale: new THREE.Vector2(0.8, 0.8) })
      : new THREE.MeshStandardMaterial({ color: liv.hull, roughness: 1.0, metalness: 0.82,
        map: hullT.albedo, normalMap: hullT.normal, roughnessMap: hullT.rough, normalScale: new THREE.Vector2(1.1, 1.1) }),
    slate: new THREE.MeshStandardMaterial({ color: 0x9aa3b0, roughness: 1.0, metalness: 0.85,
      map: greebleT.albedo, normalMap: greebleT.normal, roughnessMap: greebleT.rough, normalScale: new THREE.Vector2(1.2, 1.2) }),
    bandA: coated
      ? new THREE.MeshStandardMaterial({ color: liv.bandA, ...COATED_TRIM })
      : new THREE.MeshStandardMaterial({ color: liv.bandA, roughness: 0.45, metalness: 0.55 }),
    bandB: coated
      ? new THREE.MeshStandardMaterial({ color: liv.bandB, ...COATED_STRAKE })
      : new THREE.MeshStandardMaterial({ color: liv.bandB, roughness: 0.6, metalness: 0.4 }),
    armor: coated
      ? new THREE.MeshStandardMaterial({ color: liv.bandA, ...COATED_TRIM })
      : new THREE.MeshStandardMaterial({ color: 0xc98a5e, roughness: 1.0, metalness: 0.55,
        map: armorT.albedo, normalMap: armorT.normal, roughnessMap: armorT.rough, normalScale: new THREE.Vector2(1.4, 1.4) }),
    // near-black, high-emissivity coating: what a 950 K radiator is painted
    // with, and by Kirchhoff what lets the glow out (tools/render/hero.py matches)
    rad: new THREE.MeshStandardMaterial({ color: 0x18181a, roughness: 0.9, metalness: 0.0,
      map: radT.albedo, normalMap: radT.normal, normalScale: new THREE.Vector2(1.3, 1.3) }),
    plume: new THREE.MeshBasicMaterial({ color: liv.plumeColor ?? 0x9b6bff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }),
    port: new THREE.MeshStandardMaterial({ color: 0xffcf8f, emissive: liv.sensorGlow, emissiveIntensity: 2.2, roughness: 0.4 }),
  };
  // Object-space triplanar (no UVs, no module-boundary seams). One density
  // number per material set replaces the old per-primitive repeat tuning.
  // Phase 3 detail: per-material wear/grime — a working ship, not a showroom.
  // A weathered coating chips at its edges and streaks finely along the spine
  // (an authored hull's mesh +Y); the working ship's wear would strip whole flat panels of it.
  const coatAniso = new THREE.Vector3(2.2, 0.45, 2.2);
  applyTriplanar(mats.hull, coated
    ? { scale: 0.5, wear: 0.12 * w, grime: 0.6 * w, grimeAniso: coatAniso }
    : { scale: 0.5, wear: 0.45 * w, grime: 0.5 * w });
  applyTriplanar(mats.slate, { scale: 0.9, wear: 0.3 * w, grime: 0.6 * w });
  applyTriplanar(mats.armor, { scale: 0.9, wear: 0.65 * w, grime: 0.35 * w, wearColor: 0xd9a06a }); // copper shines through
  applyTriplanar(mats.rad, { scale: 0.8, wear: 0.2 * w, grime: 0.3 * w });
  // the glow is the rad material's only emission, and it is heat, not a ramp
  mats.radGlow = applyRadiatorGlow(mats.rad);
  // livery paint chips down to hull metal on edges; grime settles on the bands
  applyTriplanar(mats.bandA, { wear: (coated ? 0.35 : 0.55) * w, grime: 0.45 * w, grimeAniso: coated ? coatAniso : undefined });
  applyTriplanar(mats.bandB, { wear: (coated ? 0.35 : 0.55) * w, grime: 0.45 * w, grimeAniso: coated ? coatAniso : undefined });
  return mats;
}

// The slots only an authored hull paints (parts.ts PART_SLOTS): its heat tiles,
// its cargo, a lit tell and a drive throat. Only those its GLB draws are made.
type HullOwn = Partial<Record<'tile' | 'cargo' | 'bio' | 'driveGlow' | 'driveHeat' | 'glass', THREE.MeshStandardMaterial>>;
function createHullMaterials(liv: Livery, slots: readonly string[] | undefined, w: number): HullOwn {
  const wants = (k: keyof PartMats) => !slots || slots.some((s) => PART_SLOTS[s] === k);
  const own: HullOwn = {};
  if (wants('tile')) {
    const tileT = texSet('reg-coating');
    own.tile = new THREE.MeshStandardMaterial({ color: 0x2a2b2f, roughness: 1.0, metalness: 0.15,
      map: tileT.albedo, normalMap: tileT.normal, roughnessMap: tileT.rough, normalScale: new THREE.Vector2(0.9, 0.9) });
    applyTriplanar(own.tile, { scale: 1.6, wear: 0.15, grime: 0.35, wearColor: 0x55575c });
  }
  // honey seen through a pane, lit from inside the hold
  if (wants('cargo')) own.cargo = new THREE.MeshStandardMaterial({ color: 0xe0a238, roughness: 0.25, metalness: 0.0,
    emissive: 0xd9861c, emissiveIntensity: 0.6 });
  if (wants('bio')) own.bio = new THREE.MeshStandardMaterial({ color: 0x2f6b2a, emissive: 0x46c24a, emissiveIntensity: 1.4, roughness: 0.35 });
  if (wants('driveGlow')) own.driveGlow = new THREE.MeshStandardMaterial({ color: 0x101418, emissive: liv.plumeColor ?? 0x9b6bff,
    emissiveIntensity: 1.8, roughness: 0.5 });
  // a bell's bare steel, which driveHeat.ts heats from the exit up
  if (wants('driveHeat')) {
    own.driveHeat = new THREE.MeshStandardMaterial({ color: liv.bandB, roughness: 0.6, metalness: 0.45 });
    if (w > 0) applyTriplanar(own.driveHeat, { scale: 2.2, wear: 0.3 * w, grime: 0.45 * w, wearColor: 0x8a8f96 });
  }
  // a bridge's glass: dark and glossy, lit warm from the cabin behind it
  if (wants('glass')) own.glass = new THREE.MeshStandardMaterial({ color: 0x0b0e12, roughness: 0.25, metalness: 0,
    envMapIntensity: 0.4, emissive: 0xff9a4a, emissiveIntensity: 0.04 });
  return own;
}

// one sphere per lamp size for the whole fleet
const _lampGeo = new Map<number, THREE.SphereGeometry>();
function lampGeometry(r: number): THREE.SphereGeometry {
  let g = _lampGeo.get(r);
  if (!g) _lampGeo.set(r, g = new THREE.SphereGeometry(r, 6, 6));
  return g;
}

const alignZ = (m: THREE.Mesh) => { m.rotation.x = Math.PI / 2; return m; }; // orient a +Y cylinder along Z

// Red to port, green to starboard, white astern; any other station is a beacon.
// Red is driven harder: it carries little luminance, so it would not bloom like the green.
const NAV_PORT = { colour: 0xff2a1f, glow: 6 }, NAV_STARBOARD = { colour: 0x2bff6a, glow: 2.2 };
const NAV_STERN = { colour: 0xf2f4ff, glow: 2.2 };
function navLight([x = 0, , z = 0]: readonly number[]): { colour: number; glow: number } | null {
  return x < -0.5 ? NAV_PORT : x > 0.5 ? NAV_STARBOARD : z > 0.5 ? NAV_STERN : null;
}

// ---- the burn on an authored hull ----
// Every att.plume the manifest declares gets a cone, at the nozzle somebody
// marked and pointing the way they pointed it.
//
// The cone's APEX is moved onto its own origin, which the procedural drive's
// is not. That one is centred on its position and half of it lies forward of
// the bell, out of sight inside the drive block; an authored nozzle sits on
// the open hull with nothing to hide an overhang, and on the Liberdade the
// forward half came out through the cargo hold and past the bow. Pinning the
// apex also means the update loop's scale.y grows the flame aft out of the
// nozzle instead of about its middle - same one line in update(), no
// per-frame position to keep in step.
const PLUME_AXIS = new THREE.Vector3(0, 1, 0);
function authoredPlumes(g: THREE.Group, m: ModuleSpec, hull: HullManifest, mats: MatSet): THREE.Mesh[] {
  const plumes: THREE.Mesh[] = [];
  // a procedural drive block is 2.4 long at size 1 and wears a 0.16 x 3.2
  // plume - scale off the authored length so a capital's burn is a capital's
  const k = (m.length ?? 2.4) / 2.4;
  for (const a of hull.attach) {
    if (a.role !== 'plume') continue;
    // Length scales with the engine block; width is the nozzle's own, because
    // the procedural 20:1 taper that reads as flame on a frigate reads as a
    // wire at capital length.
    const len = 3.2 * k;
    const geo = new THREE.ConeGeometry(a.radius ?? 0.16 * k, len, 14, 1, true);
    geo.translate(0, -len / 2, 0);                          // apex to the origin
    const plume = new THREE.Mesh(geo, mats.plume.clone());
    // +Y is the apex end, so aiming it at -dir lays the body along dir: out of
    // the nozzle, the way the marker points.
    plume.quaternion.setFromUnitVectors(PLUME_AXIS, new THREE.Vector3(-a.dir[0], -a.dir[1], -a.dir[2]));
    plume.position.set(a.at[0], a.at[1], a.at[2] - m.z);    // the group already sits at m.z
    plume.scale.set(1, 0.001, 1);
    plume.name = 'drive-plume';   // src/scene/thrusters.ts reads the drawn burn back
    g.add(plume);
    plumes.push(plume);
  }
  return plumes;
}

// ---- one module → one named THREE.Group (positions live in the spec's `z`) ----
// Returns the group + (for the drive block) the plume meshes the loop animates.
function buildModule(m: ModuleSpec, mats: MatSet, liv: Livery, gen = 1, hull?: HullManifest): { group: THREE.Group; plumes?: THREE.Mesh[] } {
  const g = new THREE.Group();
  g.name = m.id;
  g.position.z = m.z;
  let plumes: THREE.Mesh[] | undefined;

  if (m.geometry === 'hull') {
    // The authored hull already drew this module. The group still exists, and
    // still carries the id: it is what damage wounds and what the editor
    // measures. The one thing the mesh cannot carry is the burn.
    if (m.kind === 'drive' && hull) plumes = authoredPlumes(g, m, hull, mats);
    return { group: g, plumes };
  }

  switch (m.kind) {
    case 'drive': {
      const s = m.size ?? 1.0;
      g.add(new THREE.Mesh(new THREE.BoxGeometry(1.5 * s, 1.3 * s, 2.4 * s), mats.slate));
      if (liv.reactorGlow != null) {
        // a glowing core between the reactors — e.g. the Esperança's failing seal
        const core = new THREE.Mesh(new THREE.SphereGeometry(0.5 * s, 16, 12),
          new THREE.MeshStandardMaterial({ color: liv.reactorGlow, emissive: liv.reactorGlow, emissiveIntensity: 2.6, roughness: 0.4 }));
        core.position.set(0, 0, -0.4 * s); g.add(core);
      }
      plumes = [];
      for (const x of [-0.5 * s, 0.5 * s]) {
        const reactor = alignZ(new THREE.Mesh(new THREE.CapsuleGeometry(0.42 * s, 1.2 * s, 6, 12), mats.hull));
        reactor.position.set(x, 0, -0.4 * s); g.add(reactor);
        // forged regen nozzle (kitbash part) — authored +Y, rotate to point aft
        const bell = instancePart('thruster-bell', mats);
        bell.rotation.x = Math.PI / 2; bell.position.set(x, 0, 1.0 * s);
        bell.scale.setScalar(0.55 * s); g.add(bell);
        const plume = new THREE.Mesh(new THREE.ConeGeometry(0.16 * s, 3.2 * s, 14, 1, true), mats.plume.clone());
        plume.rotation.x = -Math.PI / 2; plume.position.set(x, 0, 1.5 * s); plume.scale.set(1, 0.001, 1);
        // Named so src/scene/thrusters.ts can read the drawn main burn back.
        plume.name = 'drive-plume';
        g.add(plume); plumes.push(plume);
      }
      if (gen >= 2) {
        // GRAMMAR round 2: a thrust STRUCTURE carries the engines — conic
        // truss + heat shield + gimbal bosses, forward of the housing
        const frame = instancePart('thrust-frame', mats);
        frame.rotation.x = Math.PI / 2;            // part +Z → +Z (aft taper)
        frame.position.z = -2.55 * s;
        frame.scale.setScalar(2.0 * s);
        g.add(frame);
      }
      // attitude-control quads on the drive flanks (kitbash part, points outboard)
      for (const sx of [-1, 1]) {
        const rcs = instancePart('rcs-quad', mats);
        rcs.rotation.z = sx * -Math.PI / 2; rcs.position.set(sx * 0.75 * s, 0.3 * s, -0.7 * s);
        rcs.scale.setScalar(s); g.add(rcs);
      }
      break;
    }
    case 'tankage': {
      const len = m.length ?? 5.2;
      if (gen >= 2) {
        // GRAMMAR round 2: the tank is TILED SEGMENTS (flanged rings with
        // stringers + pipe runs), not one barrel with painted hoops
        const n = Math.max(2, Math.round(len / 1.6));
        const segH = len / n;
        for (let i = 0; i < n; i++) {
          const ring = instancePart('tank-ring', mats);
          ring.rotation.x = Math.PI / 2;           // part +Y → +Z, base at fore
          ring.rotation.z = (i % 2) * Math.PI / 4; // stagger pipe runs
          ring.position.z = -len / 2 + i * segH;
          ring.scale.set(2.0, segH, 2.0);          // r 0.5→1.0; height→segment
          g.add(ring);
        }
      } else {
        g.add(alignZ(new THREE.Mesh(new THREE.CylinderGeometry(1.0, 1.0, len, 18), mats.hull)));
      }
      const b1 = alignZ(new THREE.Mesh(new THREE.CylinderGeometry(1.02, 1.02, 0.4, 18), mats.bandA)); b1.position.z = -len * 0.29; g.add(b1);
      const b2 = alignZ(new THREE.Mesh(new THREE.CylinderGeometry(1.02, 1.02, 0.25, 18), mats.bandB)); b2.position.z = len * 0.25; g.add(b2);
      if (liv.chevron != null) {
        const chevMat = new THREE.MeshStandardMaterial({ color: liv.chevron, emissive: liv.chevron, emissiveIntensity: 0.25, roughness: 0.5 });
        for (const z of [-len * 0.6, -len * 0.02]) { const c = alignZ(new THREE.Mesh(new THREE.CylinderGeometry(1.03, 1.03, 0.5, 18), chevMat)); c.position.z = z; g.add(c); }
      }
      // ventral propellant manifold (kitbash part) — pipes run along the spine
      const manifold = instancePart('fuel-manifold', mats);
      manifold.rotation.z = Math.PI; manifold.position.set(0, -0.98, len * 0.28);
      g.add(manifold);
      if (gen >= 2) {
        // GRAMMAR: a tank is a PRESSURE VESSEL — domed ends, external
        // stringers carrying axial load (ASSEMBLY-GRAMMAR §3B)
        for (const sgn of [-1, 1]) {
          const dome = new THREE.Mesh(new THREE.SphereGeometry(1.0, 18, 10), mats.hull);
          dome.scale.z = 0.42;
          dome.position.z = sgn * len / 2;
          g.add(dome);
        }
        for (let k = 0; k < 4; k++) {
          const a = Math.PI / 4 + k * Math.PI / 2;
          const st = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.18, len * 0.94), mats.slate);
          st.position.set(Math.cos(a) * 1.0, Math.sin(a) * 1.0, 0);
          st.rotation.z = a + Math.PI / 2;
          g.add(st);
        }
      }
      break;
    }
    case 'collar': {
      g.add(alignZ(new THREE.Mesh(new THREE.CylinderGeometry(1.06, 1.06, 0.6, 18), mats.slate)));
      // lateral androgynous docking port (kitbash part) — where boarding happens
      const dock = instancePart('docking-collar', mats);
      dock.rotation.z = -Math.PI / 2; dock.position.set(1.0, 0, 0);
      dock.scale.setScalar(0.55); g.add(dock);
      // dorsal comms cluster (kitbash part) — whips + dish + status lamp
      const farm = instancePart('antenna-farm', mats);
      farm.position.set(0, 1.02, 0); g.add(farm);
      break;
    }
    case 'taper':
      g.add(alignZ(new THREE.Mesh(new THREE.CylinderGeometry(1.0, 0.7, m.length ?? 2.6, 16), mats.hull)));
      break;
    case 'bow': {
      const s = m.size ?? 1.0;
      if (gen >= 2) {
        // GRAMMAR round 2: the prow is a FORGED PART (faceted octagonal
        // terraces, dorsal visor, chin sensor) — assembly beats sculpting
        const prow = instancePart('prow-wedge', mats);
        prow.rotation.x = -Math.PI / 2;            // part +Y → -Z (forward)
        prow.position.z = 1.1 * s;                 // attach at the aft face
        prow.scale.setScalar(1.56 * s);            // base r 0.5 → hull r 0.78
        g.add(prow);
      } else {
        const cap = new THREE.Mesh(new THREE.ConeGeometry(0.78 * s, 2.2 * s, 8), mats.armor); cap.rotation.x = -Math.PI / 2; g.add(cap);
        const ring = new THREE.Mesh(new THREE.TorusGeometry(0.6 * s, 0.12 * s, 8, 16), mats.slate); ring.position.z = 1.0 * s; g.add(ring);
      }
      // 4 attitude quads around the base ring (kitbash part) — bow authority
      for (let k = 0; k < 4; k++) {
        const a = k * Math.PI / 2 + Math.PI / 4;
        const rcs = instancePart('rcs-quad', mats);
        rcs.position.set(Math.cos(a) * 0.62 * s, Math.sin(a) * 0.62 * s, 0.85 * s);
        rcs.rotation.z = a - Math.PI / 2; // local +Y points outboard radial
        rcs.scale.setScalar(0.9 * s); g.add(rcs);
      }
      break;
    }
    case 'lance': {
      const len = m.length ?? 2.4;
      const l = alignZ(new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.12, len, 12), mats.slate)); g.add(l);
      // open truss lattice around the accelerator rail (kitbash part, tiled)
      const segLen = 0.34;
      for (let z = -len / 2; z < len / 2 - 0.01; z += segLen) {
        const truss = instancePart('truss-segment', mats);
        truss.rotation.x = Math.PI / 2; truss.position.set(0, 0, z);
        truss.scale.setScalar(segLen); g.add(truss);
      }
      break;
    }
    case 'vls': {
      const cols = m.cols ?? 4, rows = m.rows ?? 8;
      const blockLen = 0.25 * rows + 0.4;
      if (gen >= 2) {
        // GRAMMAR round 2: forged missile deck — chamfered housing with
        // RECESSED cell wells (negative space sells the launcher)
        const deck = instancePart('vls-deck', mats);
        deck.rotation.x = Math.PI / 2;
        deck.position.set(0, 0.95, -blockLen / 2);
        deck.scale.set((0.28 * cols + 0.3) / 1.0, blockLen / 1.5, 1.5);
        g.add(deck);
        break;
      }
      g.add(new THREE.Mesh(new THREE.BoxGeometry(0.28 * cols + 0.3, 0.5, blockLen), mats.slate)).position.set(0, 0.95, 0);
      const cellGeo = new THREE.BoxGeometry(0.1, 0.08, 0.1);
      const cellMat = new THREE.MeshStandardMaterial({ color: 0x0a0c10, roughness: 0.9, metalness: 0.3 });
      const x0 = -0.14 * (cols - 1), z0 = -0.125 * (rows - 1);
      for (let cx = 0; cx < cols; cx++) for (let cz = 0; cz < rows; cz++) {
        const cell = new THREE.Mesh(cellGeo, cellMat);
        cell.position.set(x0 + cx * 0.28, 1.22, z0 + cz * 0.25); g.add(cell);
      }
      break;
    }
    case 'medicalPod': {
      const podMat = new THREE.MeshStandardMaterial({ color: 0xeef0f3, roughness: 0.55, metalness: 0.2 });
      const pod = new THREE.Mesh(new THREE.CapsuleGeometry(0.42, 1.6, 8, 14), podMat);
      pod.rotation.z = Math.PI / 2; pod.position.set(0, 1.05, 0); g.add(pod);
      break;
    }
    case 'sensor': {
      // forged mast (kitbash part): taper + crossbar + offset dish + glow tip
      const mast = instancePart('sensor-mast', mats);
      mast.position.set(0, 0.6, 0); mast.scale.setScalar(1.1); g.add(mast);
      if (m.expanded) {
        for (const sx of [-1, 1]) {
          const sd = new THREE.Mesh(new THREE.SphereGeometry(0.22, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), mats.port);
          sd.position.set(sx * 0.7, 0.7, 0.4); sd.rotation.set(-0.4, 0, sx * 0.6); g.add(sd);
        }
        const arrMat = new THREE.MeshStandardMaterial({ color: 0x10202a, emissive: 0x1d6f86, emissiveIntensity: 0.6, roughness: 0.4, metalness: 0.6 });
        for (const z of [2.6, 4.4]) { const p = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.06, 0.9), arrMat); p.position.set(0, 1.12, z); g.add(p); }
      }
      break;
    }
    case 'boardingBay': {
      const bay = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.5, 1.0), mats.slate);
      bay.position.set(0.85, -0.2, 0); bay.rotation.y = 0.2; g.add(bay);
      // folded grapple arm (kitbash part) on the bay face — the boarding tool
      const arm = instancePart('grapple-arm', mats);
      arm.rotation.z = -Math.PI / 2; arm.position.set(1.22, -0.2, 0.1);
      arm.scale.setScalar(0.8); g.add(arm);
      break;
    }
    case 'cargoPod': {
      if (gen >= 2) {
        // GRAMMAR: cargo rides in a SADDLE BAY — cradle sunk to the hull,
        // pods seated IN it, tie-down straps over them, and the pods pass
        // through the material system (triplanar + wear; no LEGO plastic)
        const podMat = new THREE.MeshStandardMaterial({
          color: 0xc8923c, roughness: 0.85, metalness: 0.35,
          map: texSet('greeble').albedo, normalMap: texSet('greeble').normal,
          normalScale: new THREE.Vector2(1.1, 1.1),
        });
        applyTriplanar(podMat, { scale: 1.1, wear: 0.35, grime: 0.5, wearColor: 0x8a6a3a });
        const cradle = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.34, 3.1), mats.slate);
        cradle.position.set(0, 0.78, 0); g.add(cradle);
        for (const sgn of [-1, 1]) {   // bay end bulkheads
          const bh = new THREE.Mesh(new THREE.BoxGeometry(1.24, 0.62, 0.12), mats.slate);
          bh.position.set(0, 0.92, sgn * 1.56); g.add(bh);
        }
        for (let i = 0; i < 3; i++) {
          const z = -1.0 + i * 1.0;
          const pod = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.55, 0.85), podMat);
          pod.position.set(0, 1.06, z); g.add(pod);        // seated IN the cradle
          for (const dz of [-0.28, 0.28]) {                // tie-down straps
            const strap = new THREE.Mesh(new THREE.BoxGeometry(1.12, 0.055, 0.09), mats.slate);
            strap.position.set(0, 1.34, z + dz); g.add(strap);
          }
        }
      } else {
        // honey-amber cooperative containers latched along the dorsal spine
        const podMat = new THREE.MeshStandardMaterial({
          color: 0xc8923c, roughness: 0.7, metalness: 0.25, emissive: 0x4a2e08, emissiveIntensity: 0.3,
        });
        const frameMat = new THREE.MeshStandardMaterial({ color: 0x5a4a32, roughness: 0.6, metalness: 0.5 });
        for (let i = 0; i < 3; i++) {
          const z = -1.0 + i * 1.0;
          const pod = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.55, 0.85), podMat);
          pod.position.set(0, 0.95, z); g.add(pod);
          const clamp = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.1, 0.12), frameMat);
          clamp.position.set(0, 0.66, z); g.add(clamp);
        }
      }
      break;
    }
    case 'melissa': {
      // Pet-MELiSSA closed-loop chamber — a green-glowing circular viewport
      const housing = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.34, 0.5, 14), mats.slate);
      housing.position.set(0.92, 0.2, 0); housing.rotation.z = Math.PI / 2; g.add(housing);
      const port = new THREE.Mesh(new THREE.CircleGeometry(0.24, 18),
        new THREE.MeshStandardMaterial({ color: 0x2f6b2a, emissive: 0x46c24a, emissiveIntensity: 1.4, roughness: 0.5 }));
      port.position.set(1.18, 0.2, 0); port.rotation.y = Math.PI / 2; g.add(port);
      break;
    }
  }
  return { group: g, plumes };
}

// ---- radiator rig: the animated signature subobject ----
// Gen 1 fans BoxGeometry louvres. Gen 2 seats the forged family
// (ASSEMBLY-GRAMMAR §3C): rad-root and a tiled rad-boom spar hull-fixed, the
// rad-panel louvres on the one pivot that carries the posture.
//
// NOT the composed radiator-wing GLBs: they bake one mesh per deployment
// state, so they snap where setDeploy lerps. A louvre bank rather than the
// wing's accordion because rad-panel ships with its heat gradient baked at
// panel index 0 of 5 — cloned into a chain that reads as five repeats, but
// cloned into a bank fed off one header it is simply correct.
//
// mountR is where the hardware bolts on, and it is the hull's DRESSED radius,
// not its joinery radius (spec.radiatorMountR). The two diverge sharply under
// the gen-2 grammar: a tankage interfaces at 1.0 and wears tank-ring pipe runs
// out to 1.84, so seating against the joinery figure buries the root, the whole
// spar and 93% of every louvre inside the hull.

// forged rad-panel, part units (tools/parts/build_parts.py RAD_SPAN/RAD_CHORD)
const RAD_SPAN = 1.0, RAD_CHORD = 0.742;
const ROOT_SCALE = 1.1;   // hull hardware keeps its own scale: 1.7 dwarfs a CL

function createRadiatorRig(panelsPerSide: number, mats: MatSet, scale: number, gen = 1,
                           mountR = 0.25, meshWings?: { beam: number }) {
  const radMat = mats.rad;
  const PANEL_W = 1.7, PANEL_L = 1.1, PANEL_GAP = 0.06;
  const forged = gen >= 2;
  const group = new THREE.Group(); group.name = 'radiators';
  const pivots: { g: THREE.Group; sign: number }[] = [];
  if (meshWings) {
    // The authored hull draws its own wings (HullRef.radiators === 'mesh'),
    // bound to this same rad material, so the rig adds no hardware and the
    // posture is the heat glow alone: the wings do not fold, because the mesh
    // is one posture (the cruise one). beam is the drawn span.
    return { group, setDeploy: () => +(meshWings.beam * scale).toFixed(2) };
  }
  if (panelsPerSide <= 0) {
    // hulls with no radiator rig (the authored capitals) — no-op posture
    return { group, setDeploy: () => +(2.2 * scale).toFixed(2) };
  }
  // The heat gradient rides COLOR_0 on rad-panel: three multiplies vColor
  // into diffuse (the stain), and the glow reads it as where on the coolant
  // run a face sits, inlet hot. Gen 1's boxes carry no COLOR_0, so the flag
  // has to stay off for them or they render black.
  if (forged) { radMat.vertexColors = true; radMat.needsUpdate = true; }
  // span the part to the beam the posture maths already assumes
  const partScale = PANEL_W / RAD_SPAN;
  const pitch = forged ? RAD_CHORD * partScale + PANEL_GAP : PANEL_L + PANEL_GAP;
  const totalZ = panelsPerSide * pitch;
  for (const sign of [-1, 1]) {
    const pivot = new THREE.Group();
    pivot.position.set(sign * mountR, 0.5, 1.6); group.add(pivot);
    pivot.name = `rad-louvres:${sign > 0 ? '+1' : '-1'}`;
    pivots.push({ g: pivot, sign });
    if (forged) {
      // Root and spar are hull-fixed — the louvres turn ON the spar, so the
      // rotary fluid joint has to stay collinear with the pivot axis. Both
      // parts are authored along +Y out of their mount, so one x-rotation
      // lays that axis down the ship's fold axis and the spar runs aft.
      const foreZ = 1.6 - totalZ / 2;
      const root = instancePart('rad-root', mats);
      root.rotation.x = Math.PI / 2;
      root.position.set(sign * mountR, 0.5, foreZ - 0.05);
      root.scale.setScalar(ROOT_SCALE);
      group.add(root);
      // tiled, never stretched — scaling a truss in Z stretches its root
      // fitting and clamps with it (the tank-ring idiom)
      const segs = Math.max(1, Math.ceil(totalZ / (RAD_SPAN * partScale)));
      for (let i = 0; i < segs; i++) {
        const boom = instancePart('rad-boom', mats);
        boom.rotation.x = Math.PI / 2;
        boom.position.set(sign * mountR, 0.5, foreZ + i * RAD_SPAN * partScale);
        boom.scale.setScalar(partScale);
        group.add(boom);
      }
    } else {
      // hinge hardware (kitbash part) at the fold line — static, hull-mounted
      for (const dz of [-0.45, 0.45]) {
        const hinge = instancePart('radiator-hinge', mats);
        hinge.rotation.y = Math.PI / 2; // barrel along the fold axis (Z)
        hinge.position.set(sign * mountR, 0.42, 1.6 + dz * panelsPerSide * 0.55);
        hinge.scale.setScalar(0.9);
        group.add(hinge);
      }
    }
    for (let i = 0; i < panelsPerSide; i++) {
      const z = -totalZ / 2 + pitch / 2 + i * pitch;
      if (forged) {
        // origin is on the panel's hinge axis, so it seats AT the pivot and
        // the louvre turns about the spar rather than swinging off it
        const panel = instancePart('rad-panel', mats);
        panel.rotation.y = -sign * Math.PI / 2; // span (−Z) outboard, chord → Z
        panel.position.set(0, 0, z);
        panel.scale.setScalar(partScale);
        pivot.add(panel);
        const hinge = instancePart('radiator-hinge', mats);
        hinge.rotation.y = sign * Math.PI / 2;  // axle down the fold axis, body inboard
        hinge.position.set(0, -0.05, z);
        hinge.scale.setScalar(0.34 * partScale);
        pivot.add(hinge);
      } else {
        const panel = new THREE.Mesh(new THREE.BoxGeometry(PANEL_W, 0.04, PANEL_L), radMat);
        panel.position.set(sign * (PANEL_W / 2 + 0.05), 0, z);
        pivot.add(panel);
      }
    }
  }
  /** apply deploy fraction; returns the silhouette beam width */
  function setDeploy(f: number): number {
    const ang = THREE.MathUtils.lerp(POSTURE.stowAng, POSTURE.deployAng, f);
    for (const { g, sign } of pivots) g.rotation.z = -sign * ang;
    // Gen 1 keeps its 0.3 allowance so the v1 baseline stays measurable; a
    // forged rig rides the hull's own dressed radius, so the width it returns
    // is the silhouette rather than an assumption about a slimmer hull.
    return +(2 * ((forged ? mountR : 0.3) + Math.cos(ang) * PANEL_W) * scale).toFixed(2);
  }
  return { group, setDeploy };
}

// A vane's own face on its UVs (span root to tip, chord fore to aft): a dark
// coat and its att.radiator `pipes` heat pipes off a root manifold.
const _vaneFace = new Map<number, THREE.CanvasTexture>();
function vaneFaceTexture(pipes: number): THREE.CanvasTexture {
  const hit = _vaneFace.get(pipes);
  if (hit) return hit;
  const W = 512, PITCH = 32, H = PITCH * pipes;
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#1b1e22'; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = '#44484e';
  for (let k = 0; k < pipes; k++) ctx.fillRect(0, PITCH * (k + 0.5) - 3, W, 6);
  ctx.fillRect(0, 0, 10, H);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8;
  _vaneFace.set(pipes, t);
  return t;
}

// One vane per att.radiator, authored starboard with its hinge on Z at the origin;
// port is the mirror. A part with *-outer-* meshes folds them in before the swing.
type Vane = { pivot: THREE.Group; sign: number; x: number; reach: number; fold: { group: THREE.Group; axis: THREE.Vector3 } | null };
function createVaneRig(hinges: ManifestAttach[], part: PartName, mats: MatSet, scale: number) {
  const group = new THREE.Group(); group.name = 'radiators';
  const face = new THREE.MeshStandardMaterial({ map: vaneFaceTexture(hinges[0]?.pipes ?? 1), roughness: 0.55, metalness: 0.35 });
  const glow = applyRadiatorGlow(face);
  const vaneMats = { ...mats, rad: face };
  let spanBound = false;
  let deploy = 1;
  const vanes = hinges.map((a) => {
    const sign = a.at[0] < 0 ? -1 : 1;
    const pivot = new THREE.Group();
    pivot.name = `rad-vane:${sign > 0 ? '+1' : '-1'}`;
    pivot.position.set(...a.at);
    const side = new THREE.Group();
    side.scale.x = sign;
    const v: Vane = { pivot, sign, x: Math.abs(a.at[0]), reach: a.radius ?? 1, fold: null };
    const vane = instancePart(part, vaneMats, (inst) => {
      const faces: THREE.Object3D[] = [];
      inst.traverse((o) => { if ((o as THREE.Mesh).isMesh && (o as THREE.Mesh).material === face) faces.push(o); });
      const mesh = faces[0];
      if (!spanBound && mesh) {
        // the face in the ship frame at full deploy, whatever the hinge angle now:
        // every node up to the hinge, then the hinge's place without its swing
        const toShip = new THREE.Matrix4();
        for (let o: THREE.Object3D | null = mesh; o && o !== pivot; o = o.parent) {
          o.updateMatrix();
          toShip.premultiply(o.matrix);
        }
        toShip.premultiply(new THREE.Matrix4().makeTranslation(pivot.position));
        glow.setSpan(Math.abs(a.at[0]), Math.abs(a.at[0]) + v.reach, toShip);
        spanBound = true;
      }
      v.fold = foldOf(inst);
      pose(v, deploy);
    }, true);
    vane.rotation.x = Math.PI / 2;   // the forge's frame, as the hull's
    side.add(vane); pivot.add(side); group.add(pivot);
    return v;
  });
  // the outer panel moves under a group turning about the barrel's axis, which
  // runs along the instance's Y as the root hinge does
  function foldOf(inst: THREE.Object3D): Vane['fold'] {
    const outer: THREE.Object3D[] = [];
    let barrel: THREE.Mesh | undefined;
    inst.traverse((o) => {
      if (o.name.includes('-outer-')) outer.push(o);
      else if (o.name.endsWith('-fold-hinge') && (o as THREE.Mesh).isMesh) barrel = o as THREE.Mesh;
    });
    if (!barrel?.parent || !outer.length) return null;
    barrel.geometry.computeBoundingBox();
    barrel.updateMatrix();
    const axis = barrel.geometry.boundingBox!.getCenter(new THREE.Vector3()).applyMatrix4(barrel.matrix);
    const fold = new THREE.Group(); fold.name = 'rad-fold';
    barrel.parent.add(fold);
    for (const o of outer) fold.add(o);
    return { group: fold, axis };
  }
  // the vane's lateral reach from its hinge at deploy fraction f
  function pose(v: Vane, f: number): number {
    const s = 1 - f, fold = v.fold;
    const swing = POSTURE.vaneStowAng * (fold ? THREE.MathUtils.smoothstep(s, ...POSTURE.vaneSwing) : s);
    v.pivot.rotation.z = -v.sign * swing;
    if (!fold) return Math.cos(swing) * v.reach;
    const t = Math.PI * THREE.MathUtils.smoothstep(s, ...POSTURE.vaneFold);
    const c = Math.cos(t), n = Math.sin(t), { x, z } = fold.axis;
    fold.group.rotation.y = t;
    fold.group.position.set(x - (x * c + z * n), 0, z - (z * c - x * n));
    return Math.cos(swing) * (x + Math.max(0, c) * (v.reach - x));
  }
  function setDeploy(f: number): number {
    deploy = f;
    return +(2 * Math.max(...vanes.map((v) => v.x + pose(v, f))) * scale).toFixed(2);
  }
  return { group, setDeploy, face, glow };
}

// The call sites' radii are the procedural frigate's; an authored hull is seated on
// its mesh once it lands, since rBody is a vertex percentile that off-skin parts inflate.
const DECAL_STANDOFF = 0.03;
function decalRadius(spec: ShipSpec, m: ModuleSpec, z: number, fallback: number): number {
  if (m.geometry !== 'hull') return fallback;
  return bodyRadiusAt(spec, z, fallback - DECAL_STANDOFF) + DECAL_STANDOFF;
}
function bodyRadiusAt(spec: ShipSpec, z: number, fallback: number): number {
  const p = spec.hull?.manifest.profile;
  if (!p) return fallback;
  const i = Math.min(Math.max(Math.floor((z - p.z0) / p.pitch), 0), p.rBody.length - 1);
  return p.rBody[i] ?? fallback;
}

const hexLum = (hex: number): number => ((hex >> 16 & 0xff) * 0.3 + (hex >> 8 & 0xff) * 0.59 + (hex & 0xff) * 0.11) / 255;
// dark ink on a light surface (the hospital-ship white-on-white lesson)
const inkOn = (lum: number): string => lum > 0.62 ? '#232c3a' : '#dfe6f0';

// Rays from outside the hull toward its skin; the median hit seats the decal. Rays
// that mostly disagree (KitBash greebles, an open frame) mean no skin: not painted.
interface DecalSeat {
  rays: THREE.Ray[];
  radial: boolean;
  apply: (r: number, surfaceLum: number | null) => void;
}
const SEAT_LIFT = 0.005;
const SEAT_AGREE = 0.06;
// a hull seats a decal once a session, whatever the fleet size; the surface is
// kept as the hit mesh's place in the hull, since each ship paints it its own colour
const _seatCache = new Map<string, { r: number; mesh: number } | null>();

function radialRay(phi: number, z: number, reach: number): THREE.Ray {
  const c = Math.cos(phi), s = Math.sin(phi);
  return new THREE.Ray(new THREE.Vector3(c * reach, s * reach, z), new THREE.Vector3(-c, -s, 0));
}

function seatDecals(hull: string, inst: THREE.Object3D, visual: THREE.Object3D, seats: DecalSeat[]): void {
  inst.updateWorldMatrix(true, true);
  const meshes: THREE.Object3D[] = [];
  inst.traverse((o) => { if ((o as THREE.Mesh).isMesh) meshes.push(o); });
  const rc = new THREE.Raycaster();
  for (const seat of seats) {
    const key = `${hull}|${seat.radial ? 'r' : 'x'}|` +
      seat.rays.map((r) => r.origin.toArray().map((v) => v.toFixed(3)).join(',')).join(';');
    let hit = _seatCache.get(key);
    if (hit === undefined) {
      const found: { v: number; mesh: number }[] = [];
      for (const ray of seat.rays) {
        rc.ray.copy(ray).applyMatrix4(visual.matrixWorld);
        const first = rc.intersectObject(inst, true)[0];
        if (!first) continue;
        const p = visual.worldToLocal(first.point.clone());
        found.push({ v: seat.radial ? Math.hypot(p.x, p.y) : Math.abs(p.x), mesh: meshes.indexOf(first.object) });
      }
      found.sort((a, b) => a.v - b.v);
      const mid = found[Math.floor(found.length / 2)];
      const agree = mid ? found.filter((f) => Math.abs(f.v - mid.v) <= SEAT_AGREE).length : 0;
      hit = mid && 2 * agree > seat.rays.length ? { r: mid.v + SEAT_LIFT, mesh: mid.mesh } : null;
      _seatCache.set(key, hit);
    }
    const mat = hit ? (meshes[hit.mesh] as THREE.Mesh | undefined)?.material : undefined;
    // under an albedo map the colour is a tint, not what the eye sees: a pale
    // tint on a dark plate picked dark ink and lost the hull number
    const std = mat && !Array.isArray(mat) ? mat as THREE.MeshStandardMaterial : undefined;
    const col = std && !std.map ? std.color : undefined;
    seat.apply(hit?.r ?? NaN, col ? hexLum(col.getHex()) : null);
  }
}

// ---- GRAMMAR §3A: the joinery pass (generation 2+) ----
// Hull-forming modules expose fore/aft interface rings; every adjacent pair
// gets a flange ring, and any gap gets a conic adapter — a gen-2 hull CANNOT
// have a hole in it. (The v1 Pup and Misericórdia literally did.)
const HULL_IFACE: Partial<Record<ModuleKind, (m: ModuleSpec) => { foreZ: number; foreR: number; aftZ: number; aftR: number }>> = {
  drive: (m) => { const s = m.size ?? 1; return { foreZ: m.z - 1.2 * s, foreR: 0.7 * s, aftZ: m.z + 1.2 * s, aftR: 0 }; },
  tankage: (m) => { const l = m.length ?? 5.2; return { foreZ: m.z - l / 2, foreR: 1.0, aftZ: m.z + l / 2, aftR: 1.0 }; },
  collar: (m) => ({ foreZ: m.z - 0.3, foreR: 1.06, aftZ: m.z + 0.3, aftR: 1.06 }),
  taper: (m) => { const l = m.length ?? 2.6; return { foreZ: m.z - l / 2, foreR: 0.7, aftZ: m.z + l / 2, aftR: 1.0 }; },
  bow: (m) => { const s = m.size ?? 1; return { foreZ: m.z - 1.1 * s, foreR: 0, aftZ: m.z + 1.1 * s, aftR: 0.78 * s }; },
};

function joineryPass(spec: ShipSpec, visual: THREE.Group, mats: MatSet): void {
  const chain = spec.modules
    // an authored module's hull is already continuous - drawing procedural
    // flanges and adapters onto it would be furniture from another ship
    .filter((m) => m.geometry !== 'hull')
    .map((m) => HULL_IFACE[m.kind]?.(m))
    .filter((i): i is NonNullable<typeof i> => !!i)
    .sort((a, b) => a.foreZ - b.foreZ);
  for (let k = 0; k < chain.length - 1; k++) {
    const A = chain[k]!;            // forward module (smaller z)
    const B = chain[k + 1]!;
    const rFore = A.aftR || B.foreR;
    const rAft = B.foreR || A.aftR;
    const gap = B.foreZ - A.aftZ;
    if (gap > 0.05) {
      // conic adapter: aft radius at +Z ⇒ CylinderGeometry(top=rAft aft side)
      const ad = alignZ(new THREE.Mesh(new THREE.CylinderGeometry(rAft, rFore, gap + 0.08, 18), mats.hull));
      ad.position.z = (A.aftZ + B.foreZ) / 2;
      ad.name = 'joinery-adapter';
      visual.add(ad);
    }
    const fz = gap > 0.05 ? B.foreZ : (A.aftZ + B.foreZ) / 2;
    const fr = Math.max(rFore, rAft) + 0.045;
    const flange = alignZ(new THREE.Mesh(new THREE.CylinderGeometry(fr, fr, 0.15, 18), mats.slate));
    flange.position.z = fz;
    flange.name = 'joinery-flange';
    visual.add(flange);
  }
}

let radShipSerial = 0;

export function createCustos(opts: CustosOptions = {}): Custos {
  const { scale = 1, initialDeploy = 1 } = opts;
  const spec = resolveSpec(opts.variant);
  const gen = spec.generation ?? 1;
  const pennant = opts.pennant ?? spec.pennantDefault;
  const showSigil = opts.sigil ?? spec.sigil;

  const root = new THREE.Group();
  const visual = new THREE.Group();
  visual.scale.setScalar(scale);
  root.add(visual);

  const grit = Math.max(opts.grit ?? 1, 0);
  const mats = createMaterials(spec.livery, grit);
  const disposables: { dispose: () => void }[] = [];
  const seats: DecalSeat[] = [];
  let disposed = false;
  let hullDamage: HullDamage | null = null;
  let driveHeat: DriveHeat | undefined;

  // ---- the authored hull: one GLB, drawn ONCE, at ship level ----
  // Its modules sit in spec.modules like any others and carry identity, not
  // geometry (ModuleSpec.geometry === 'hull'), so this is the only place the
  // mesh enters the scene and buildModule draws nothing for them.
  if (spec.hull) {
    const man = spec.hull.manifest;
    const own = createHullMaterials(spec.livery, man.slots, grit);
    // a drive to heat only where the GLB is known to draw a throat or a bell
    if (opts.driveHeat !== false && man.slots && (own.driveGlow || own.driveHeat)) {
      driveHeat = createDriveHeat({ throat: own.driveGlow, skin: own.driveHeat, coolColor: spec.livery.plumeColor ?? 0x9b6bff,
        grit, soot: [mats.hull, mats.slate, mats.armor, mats.bandA, mats.bandB] });
    }
    let port = mats.port;
    if (spec.hull.glow != null) {
      port = mats.port.clone();
      port.emissiveIntensity = spec.hull.glow;   // tames a big glass canopy
    }
    const hullMats = { ...mats, ...own, port };
    // the GLB may never land before dispose, so these are not left to the mesh walk
    for (const m of [...Object.values(own), port]) if (m !== mats.port) disposables.push(m);
    const holder = new THREE.Group();
    holder.name = 'hull';
    // 180° about the dorsal axis for a hull the ingest put in nose-aft. It is
    // declared in the manifest and not here so that the measured profile goes
    // through the same rotation (tools/parts/hull_profile.mjs).
    if (man.flip) holder.rotation.y = Math.PI;
    const part = instancePart(man.hull as PartName, hullMats, (inst) => {
      if (!disposed) seatDecals(man.hull, inst, visual, seats);
    }, true);
    part.rotation.x = Math.PI / 2;
    holder.add(part);
    visual.add(holder);
    // an authored module has no mesh of its own to char, so the hull chars its band
    hullDamage = createHullDamage(spec.modules.filter((m) => m.geometry === 'hull'), visual);
    for (const m of new Set([hullMats.hull, hullMats.slate, hullMats.armor, hullMats.port, hullMats.rad,
      hullMats.bandA, hullMats.bandB, ...Object.values(own)])) hullDamage.patch(m);
    disposables.push(hullDamage);
  }

  // walk the spec's module stack → named groups
  const plumes: THREE.Mesh[] = [];
  const moduleGroups = new Map<string, { group: THREE.Group; spec: ModuleSpec }>();
  for (const m of spec.modules) {
    const built = buildModule(m, mats, spec.livery, gen, spec.hull?.manifest);
    visual.add(built.group);
    moduleGroups.set(m.id, { group: built.group, spec: m });
    if (built.plumes) plumes.push(...built.plumes);
  }
  driveHeat?.setExits(plumes);

  if (gen >= 2) joineryPass(spec, visual, mats);

  // radiator rig (the signature)
  const hinges = spec.hull?.manifest.attach.filter((a) => a.role === 'radiator') ?? [];
  const vane = spec.hull?.radiator;
  const meshWings = spec.hull?.radiators === 'mesh'
    ? { beam: 2 * Math.max(...spec.hull.manifest.profile.hw) } : undefined;
  // The wings run from the body's own half-width (the profile's median
  // station) out to the drawn beam, and the loop enters at the root. The GLB
  // lands asynchronously, so update() binds the span once a wing mesh exists.
  let wingSpan: { root: number; tip: number } | null = null;
  if (meshWings && spec.hull) {
    const hw = [...spec.hull.manifest.profile.hw].sort((a, b) => a - b);
    wingSpan = { root: hw[Math.floor(hw.length / 2)] ?? 0, tip: meshWings.beam / 2 };
  }
  const bindWingSpan = (): void => {
    if (!wingSpan || !mats.radGlow) return;
    const wings: THREE.Object3D[] = [];
    visual.traverse((o) => { if ((o as THREE.Mesh).isMesh && (o as THREE.Mesh).material === mats.rad) wings.push(o); });
    const wing = wings[0];
    if (!wing) return;
    // the mesh's fixed place in the ship frame, from local matrices only, so
    // neither the ship's flight nor its idle bob enters it
    const toShip = new THREE.Matrix4();
    for (let o: THREE.Object3D | null = wing; o && o !== visual; o = o.parent) {
      o.updateMatrix();
      toShip.premultiply(o.matrix);
    }
    mats.radGlow.setSpan(wingSpan.root, wingSpan.tip, toShip);
    wingSpan = null;
    // the running lights go on the drawn tips, a hand's width outboard
    if (navLights) {
      const v = new THREE.Vector3();
      const port = new THREE.Vector3(Infinity, 0, 0), stbd = new THREE.Vector3(-Infinity, 0, 0);
      for (const w of wings) {
        const m = new THREE.Matrix4();
        for (let o: THREE.Object3D | null = w; o && o !== visual; o = o.parent) { o.updateMatrix(); m.premultiply(o.matrix); }
        const pos = (w as THREE.Mesh).geometry.getAttribute('position');
        for (let i = 0; i < pos.count; i++) {
          v.fromBufferAttribute(pos, i).applyMatrix4(m);
          if (v.x < port.x) port.copy(v);
          if (v.x > stbd.x) stbd.copy(v);
        }
      }
      if (Number.isFinite(port.x) && Number.isFinite(stbd.x)) {
        port.x -= 0.03; stbd.x += 0.03;
        navLights.setRunning(port, stbd);
      }
    }
  };
  // An authored hull that places its own lamps (att.light) keeps to them;
  // one that does not gets running lights, strobes, work lamps and habitat windows.
  const lights = spec.hull?.manifest.attach.filter((a) => a.role === 'light') ?? [];
  let navLights: NavLights | null = null;
  if (spec.hull && !lights.length) {
    navLights = createNavLights(spec.hull.manifest, radShipSerial);
    if (navLights.object) visual.add(navLights.object);
    disposables.push(navLights);
  }
  const rig: { group: THREE.Group; setDeploy: (f: number) => number; face?: THREE.MeshStandardMaterial; glow?: RadiatorGlow } =
    vane !== undefined && hinges.length && spec.radiatorPanelsPerSide > 0
    ? createVaneRig(hinges, vane, mats, scale)
    : createRadiatorRig(spec.radiatorPanelsPerSide, mats, scale, gen,
                        spec.radiatorMountR ?? 0.25, meshWings);
  visual.add(rig.group);
  if (rig.face) {
    hullDamage?.patch(rig.face);
    disposables.push(rig.face);
  }
  const radGlows = [mats.radGlow, rig.glow].filter((g): g is RadiatorGlow => g !== undefined);
  // every radiator mesh gets its own coolant-slug clock, and this ship its own
  // run of them; an authored wing lands with its GLB, so keep looking till then
  const shipSeed = (radShipSerial++ % 997) * 7.31;
  let radSeeded = false;
  let radSeedTries = 0;
  const seedRadiators = (): void => {
    if (!radGlows.length) { radSeeded = true; return; }
    let i = 0;
    visual.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const glow = mesh.material === mats.rad ? mats.radGlow : rig.face && mesh.material === rig.face ? rig.glow : undefined;
      glow?.seedMesh(o, shipSeed + 1.713 * i++);
    });
    // a ship with no radiator mesh at all stops looking after a few seconds
    radSeeded = (i > 0 && !wingSpan) || ++radSeedTries > 600;
  };

  // running lights along the spine. An authored hull places its own: its
  // profile's half-height is a mast's or a radiator's as often as the spine's.
  const lamps = lights.length
    ? lights.map((a) => ({ at: a.at.map((v, k) => v + 0.05 * (a.dir[k] ?? 0)), nav: navLight(a.dir) }))
    : [-1.5, 0.5, 2.5, 4.5].map((z) => ({ at: [0, 1.05, z], nav: null }));
  const navMats = new Map<number, THREE.MeshStandardMaterial>();
  const navMat = ({ colour, glow }: { colour: number; glow: number }): THREE.MeshStandardMaterial => {
    let m = navMats.get(colour);
    if (!m) navMats.set(colour, m = new THREE.MeshStandardMaterial({ color: colour, emissive: colour, emissiveIntensity: glow, roughness: 0.4 }));
    return m;
  };
  for (const { at: [x = 0, y = 0, z = 0], nav } of lamps) {
    const lite = new THREE.Mesh(lampGeometry(0.05), nav === null ? mats.port : navMat(nav));
    lite.userData.sharedGeometry = true;
    lite.position.set(x, y, z); visual.add(lite);
  }

  // ---- decals (livery as data) ----
  const decalMaterial = makeDecalMaterial;
  const reach = Math.max(...(spec.hull?.manifest.profile.r ?? [2])) + 1;
  // an authored hull's decals stay hidden until its mesh lands and seats them
  const seat = (mesh: THREE.Mesh, rays: THREE.Ray[], radial: boolean, apply: DecalSeat['apply']): void => {
    if (!spec.hull) return;
    mesh.visible = false;
    seats.push({ rays, radial, apply: (r, lum) => {
      mesh.userData.unseated = !Number.isFinite(r);
      if (mesh.userData.unseated) return;
      apply(r, lum);
      mesh.visible = true;
    } });
  };
  // two seats, one each end, for a shell that follows a tapering hull
  const seatTapered = (mesh: THREE.Mesh, fore: THREE.Ray[], aft: THREE.Ray[],
    apply: (rFore: number, rAft: number, lum: number | null) => void): void => {
    if (!spec.hull) return;
    mesh.visible = false;
    let rFore = NaN;
    seats.push({ rays: fore, radial: true, apply: (r) => { rFore = r; } });
    seats.push({ rays: aft, radial: true, apply: (r, lum) => {
      mesh.userData.unseated = !Number.isFinite(rFore) || !Number.isFinite(r);
      if (mesh.userData.unseated) return;
      apply(rFore, r, lum);
      mesh.visible = true;
    } });
  };
  const reink = (mesh: THREE.Mesh, lum: number | null, was: string, paint: (ink: string) => THREE.CanvasTexture): void => {
    if (lum === null || inkOn(lum) === was) return;
    const tex = paint(inkOn(lum));
    if (!tex.userData.shared) disposables.push(tex);
    (mesh.material as THREE.MeshStandardMaterial).map = tex;
  };
  // rotation.x = -PI/2 puts theta at visual angle PI/2 - theta, +PI/2 at theta - PI/2.
  const shellRays = (thetaDeg: number, arcDeg: number, z: number, height: number, sense: 1 | -1, spin = 0): THREE.Ray[] => {
    const rays: THREE.Ray[] = [];
    for (const dt of [-arcDeg / 3, 0, arcDeg / 3]) {
      const phi = sense * (THREE.MathUtils.degToRad(thetaDeg + dt) - Math.PI / 2) + spin;
      for (const dz of [-height / 3, 0, height / 3]) rays.push(radialRay(phi, z + dz, reach));
    }
    return rays;
  };
  const reshell = (mesh: THREE.Mesh, r: number, height: number, arcDeg: number, thetaDeg: number): void => {
    mesh.geometry.dispose();
    mesh.geometry = curvedDecalGeometry(r, height, arcDeg, thetaDeg);
  };

  // Each side paints its own shells: spinning one onto the other, as the house mark
  // does, would stand the line on its head.
  const flankLine = (name: string, text: string, style: 'brush' | 'stencil',
    at: { z: number; span: number; band: number; elevation: number }, color?: number): void => {
    const words = text.split(/\s+/).filter(Boolean);
    const css = color === undefined ? undefined : `#${color.toString(16).padStart(6, '0')}`;
    const gap = 0.25;                              // between words, in bands
    for (const turn of [-1, 1] as const) {
      const theta = turn < 0 ? 90 - at.elevation : 270 + at.elevation;
      const paint = (w: string, color?: string): THREE.CanvasTexture => lineTexture(w, { turn, style, color });
      const texs = words.map((w) => paint(w, css));
      const aspect = texs.map((t) => { const c = t.image as HTMLCanvasElement; return c.height / c.width; });
      const band = Math.min(at.band, at.span / (aspect.reduce((a, b) => a + b, 0) + gap * (words.length - 1)));
      const total = band * (aspect.reduce((a, b) => a + b, 0) + gap * (words.length - 1));
      // a line reads toward the bow to starboard and toward the stern to port
      let z = at.z - turn * total / 2;
      texs.forEach((tex, i) => {
        const h = band * (aspect[i] ?? 0);
        const zc = z + turn * h / 2;
        z += turn * (h + gap * band);
        const r = spec.hull ? bodyRadiusAt(spec, zc, 0.86 - DECAL_STANDOFF) + DECAL_STANDOFF : 0.86;
        const arc = THREE.MathUtils.radToDeg(band / r);
        const mesh = curvedDecal(r, h, arc, theta, tex);
        mesh.name = name;
        mesh.rotation.x = -Math.PI / 2;            // the cylinder's top toward the bow
        mesh.position.z = zc;
        visual.add(mesh);
        seatTapered(mesh, shellRays(theta, arc, zc - h / 3, 0.2 * h, -1), shellRays(theta, arc, zc + h / 3, 0.2 * h, -1),
          (rFore, rAft, lum) => {
            const k = (rAft - rFore) / (2 * h / 3);
            mesh.geometry.dispose();
            mesh.geometry = curvedDecalGeometry(rFore - k * h / 6, h, arc, theta, rAft + k * h / 6);
            if (style === 'stencil') reink(mesh, lum, '#dfe6f0', (ink) => paint(words[i] ?? '', ink));
          });
      });
    }
  };

  const pennantZ = spec.pennantZ ?? -1.9;
  if (spec.livery.pennantStyle === 'name') {
    flankLine('decal:pennant', pennant, 'stencil', { z: pennantZ, span: 1.4, band: 0.22, elevation: 32 });
  } else {
    const pennantTex = makePennantTexture(pennant);
    disposables.push(pennantTex);
    const pennantX = bodyRadiusAt(spec, pennantZ, 0.86 - DECAL_STANDOFF) + DECAL_STANDOFF;
    for (const side of [-1, 1]) {
      const plate = new THREE.Mesh(new THREE.PlaneGeometry(0.9, 0.45), decalMaterial(pennantTex));
      plate.name = 'decal:pennant';
      plate.position.set(side * pennantX, 0.18, pennantZ); plate.rotation.y = side * Math.PI / 2; visual.add(plate);
      const rays: THREE.Ray[] = [];
      for (const dy of [-0.12, 0, 0.12]) {
        for (const dz of [-0.3, 0, 0.3]) {
          rays.push(new THREE.Ray(new THREE.Vector3(side * reach, 0.18 + dy, pennantZ + dz), new THREE.Vector3(-side, 0, 0)));
        }
      }
      seat(plate, rays, false, (r, lum) => {
        plate.position.x = side * r;
        reink(plate, lum, '#dfe6f0', (ink) => makePennantTexture(pennant, ink));
      });
    }
  }
  if (showSigil) {
    const badge = new THREE.Mesh(new THREE.PlaneGeometry(0.72, 0.72), decalMaterial(sigilTexture()));
    badge.position.set(0, 0.92, -2.35); badge.rotation.x = -Math.PI / 2.2; visual.add(badge);
  }

  // ---- the decal game: naval typography + function-tracing hazard ----
  // House mark stacked top-to-bottom on both tankage flanks + BIG pennant,
  // sized for another ship's optics. Hazard striping wraps the collar arc
  // facing the docking port and frames the boarding bay — accent color
  // traces FUNCTION (docking/boarding zones), never decoration.
  const tank = spec.modules.find((m) => m.kind === 'tankage');
  if (tank && spec.livery.houseMark) {
    const houseMark = spec.livery.houseMark;
    const len = tank.length ?? 5.2;
    const inkColor = inkOn(hexLum(spec.livery.hull));
    const numText = pennant.replace(/[^0-9]/g, '') || pennant;
    const markTex = bannerTexture(houseMark, { vertical: true, worn: 0.4, color: inkColor });
    const numTex = bannerTexture(numText, { vertical: true, worn: 0.3, color: inkColor });
    disposables.push(markTex, numTex);
    // Cylinder theta after the spine rotation (rotation.x = π/2):
    // θ=0 → ventral, θ=90 → +X flank (dock side), θ=180 → dorsal, θ=270 → -X.
    const markH = Math.min(len * 0.62, 0.42 * houseMark.length);
    // arc width follows per-character height, else long names on short tankage
    // squash into sideways-reading glyphs (the LIGA AUREA lesson)
    const charH = markH / houseMark.length;
    const markArc = Math.min(38, THREE.MathUtils.radToDeg(charH * 1.25));
    const numDigits = Math.max(numText.length, 1);
    const numArc = Math.min(34, THREE.MathUtils.radToDeg((1.5 / numDigits) * 1.25));
    // Port flank: the SAME readable starboard shell, rotated 180° about the
    // spine (a proper rotation preserves readability — no texture mirroring;
    // it's exactly how a painter walks around the hull). θ=72 pre-rotation
    // lands slightly-dorsal starboard → slightly-ventral port after the flip.
    const buildFlank = (centerDeg: number, spin: number): THREE.Group => {
      const g = new THREE.Group();
      g.rotation.z = spin;
      const markZ = tank.z + len * 0.16, numZ = tank.z - len * 0.30;
      const shells = [
        { z: markZ, height: markH, arc: markArc, text: houseMark, worn: 0.4, tex: markTex },
        { z: numZ, height: 1.5, arc: numArc, text: numText, worn: 0.3, tex: numTex },
      ];
      for (const sh of shells) {
        const mesh = curvedDecal(decalRadius(spec, tank, sh.z, 1.0), sh.height, sh.arc, centerDeg, sh.tex);
        mesh.name = 'decal:mark';
        mesh.rotation.x = -Math.PI / 2;              // texture top reads toward the BOW
        mesh.position.z = sh.z;
        g.add(mesh);
        seat(mesh, shellRays(centerDeg, sh.arc, sh.z, sh.height, -1, spin), true, (r, lum) => {
          reshell(mesh, r, sh.height, sh.arc, centerDeg);
          reink(mesh, lum, inkColor, (ink) => bannerTexture(sh.text, { vertical: true, worn: sh.worn, color: ink }));
        });
      }
      return g;
    };
    visual.add(buildFlank(108, 0));                // starboard, slightly ventral
    visual.add(buildFlank(72, Math.PI));           // spin around the spine → port side
  }
  // The motto in brush paint down each flank above the chine
  if (tank && spec.livery.motto) {
    const len = tank.length ?? 5.2;
    flankLine('decal:motto', spec.livery.motto, 'brush', { z: tank.z - 0.08 * len, span: 0.8 * len, band: 0.24, elevation: 28 },
      spec.livery.mottoColor);
  }
  const collar = spec.modules.find((m) => m.kind === 'collar');
  // a port the manifest sizes is framed by a ring on the skin around it; the
  // collar band is sized for the procedural frigate's collar
  const ports = spec.hull?.manifest.attach.filter((a) => a.role === 'dock' && a.radius !== undefined) ?? [];
  for (const d of ports) {
    const ri = (d.radius ?? 0) + 0.02, ro = ri + 0.09;
    const ringTex = hazardRingTexture(ri / ro);
    disposables.push(ringTex);
    const phi = Math.atan2(d.at[1], d.at[0]), z = d.at[2];
    const theta = THREE.MathUtils.radToDeg(phi) + 90;
    const arcAt = (r: number): number => THREE.MathUtils.radToDeg(2 * ro / r);
    const r0 = bodyRadiusAt(spec, z, Math.hypot(d.at[0], d.at[1]));
    const ring = curvedDecal(r0, 2 * ro, arcAt(r0), theta, ringTex);
    ring.name = 'decal:dock-ring';
    ring.rotation.x = Math.PI / 2;
    ring.position.z = z;
    visual.add(ring);
    // eight rays round the band's middle, clear of the port itself
    const mid = (ri + ro) / 2;
    const rays = Array.from({ length: 8 }, (_, k) => {
      const a = (k / 8) * Math.PI * 2;
      return radialRay(phi + (mid * Math.cos(a)) / r0, z + mid * Math.sin(a), reach);
    });
    seat(ring, rays, true, (r) => reshell(ring, r, 2 * ro, arcAt(r), theta));
  }
  const bays = spec.modules.filter((m) => m.kind === 'boardingBay');
  if (collar && (!ports.length || bays.length)) {
    const hazTex = hazardStripeTexture();
    disposables.push(hazTex);
    if (!ports.length) {
      // hazard arc on the collar face around the lateral docking port (+X = θ 90)
      const haz = curvedDecal(decalRadius(spec, collar, collar.z, 1.06), 0.5, 80, 90, hazTex);
      haz.name = 'decal:collar';
      haz.rotation.x = Math.PI / 2;
      haz.position.z = collar.z;
      visual.add(haz);
      if (collar.geometry === 'hull') {
        seat(haz, shellRays(90, 80, collar.z, 0.5, 1), true, (r) => reshell(haz, r, 0.5, 80, 90));
      }
    }
    for (const m of bays) {                        // hazard frame over each bay hatch
      const frame = new THREE.Mesh(new THREE.PlaneGeometry(1.05, 0.22), decalMaterial(hazTex));
      frame.position.set(decalRadius(spec, m, m.z, 1.213), -0.2 + 0.36, m.z); frame.rotation.y = Math.PI / 2; frame.rotation.z = Math.PI / 2;
      const frame2 = frame.clone();
      frame2.position.y = -0.2 - 0.36;
      visual.add(frame, frame2);
    }
  }

  // ---- state machine ----
  const state = {
    deployFraction: initialDeploy, deployTarget: initialDeploy, throttle: 0.15, beam: 0,
    variantId: spec.id, variantName: spec.name,
  };
  state.beam = rig.setDeploy(initialDeploy);
  const heat = createHeatState(spec.radiatorPanelsPerSide);

  // ---- radiator temperature: read off `heat`, never written to it ----
  // A posture applied at once (a build, a restored save) puts the panels at
  // the temperature that posture holds them at: on the loop, or stone cold.
  const radiator: { panelK: number; loopK: number } = { panelK: RADIATOR.coldK, loopK: RADIATOR.coldK };
  const settleRadiator = (fraction: number): void => {
    radiator.loopK = loopTemperature(heatFrac(heat));
    radiator.panelK = fraction >= 0.5 ? radiator.loopK : RADIATOR.coldK;
    for (const g of radGlows) g.setTemperature(radiator.panelK);
  };
  settleRadiator(initialDeploy);

  function setDeployTarget(f: number): void { state.deployTarget = THREE.MathUtils.clamp(f, 0, 1); }
  function restoreDeploy(fraction: number, target: number): void {
    state.deployFraction = fraction;
    state.deployTarget = target;
    state.beam = rig.setDeploy(fraction);
    settleRadiator(fraction);
  }
  function toggleDeploy(): void { setDeployTarget(state.deployTarget > 0.5 ? 0 : 1); }
  function setThrottle(t: number): void { state.throttle = THREE.MathUtils.clamp(t, 0, 1); }

  // ---- module damage model (the SFC lineage payoff, materialized) ----
  // One roll wounds the named 3D group here AND the derived SSD cell (the
  // caller relays via onChange). Char is a lazy per-mesh material clone with
  // the triplanar shader re-applied at cranked grime — battle damage IS
  // weathering. Red modules get a flickering ember; destroyed go dark.
  const DAMAGE_LADDER: ModuleDamageState[] = ['green', 'yellow', 'red', 'destroyed'];
  const CHAR_TINT: Record<ModuleDamageState, number> = { green: 1, yellow: 0.7, red: 0.42, destroyed: 0.16 };
  const dmgStates = new Map<string, ModuleDamageState>(spec.modules.map((m) => [m.id, 'green']));
  const embers = new Map<string, THREE.Mesh>();
  let dmgCb: ((hit: ModuleHit) => void) | null = null;

  function applyModuleVisual(id: string, st: ModuleDamageState): void {
    const entry = moduleGroups.get(id);
    if (!entry) return;
    const k = CHAR_TINT[st];
    entry.group.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh || Array.isArray(mesh.material)) return;
      let mat = mesh.material as THREE.MeshStandardMaterial;
      if (!mesh.userData.charClone) {
        mat = mat.clone();
        // Material.clone drops onBeforeCompile — re-patch; heavy grime = char
        if ((mat as THREE.Material).type === 'MeshStandardMaterial' && mat.map !== undefined) {
          applyTriplanar(mat, { scale: 0.7, wear: 0.25, grime: 0.8 });
        }
        mesh.userData.charClone = true;
        mesh.userData.baseColor = mat.color?.clone();
        mesh.userData.baseEmissive = mat.emissiveIntensity ?? 1;
        mesh.material = mat;
      }
      if (mat.color && mesh.userData.baseColor) mat.color.copy(mesh.userData.baseColor as THREE.Color).multiplyScalar(k);
      if (mat.emissiveIntensity !== undefined) {
        mat.emissiveIntensity = (mesh.userData.baseEmissive as number)
          * (st === 'destroyed' ? 0 : st === 'red' ? 0.25 : k);
      }
      mat.needsUpdate = true;
    });
    if (entry.spec.geometry === 'hull') hullDamage?.setState(id, st);
    const existing = embers.get(id);
    if (st === 'red' && !existing && entry.spec.geometry !== 'hull') {
      const ember = new THREE.Mesh(
        new THREE.SphereGeometry(0.16, 8, 6),
        new THREE.MeshBasicMaterial({ color: 0xff5522, transparent: true, opacity: 0.9 }),
      );
      ember.position.set(0.2, 0.3, 0);
      entry.group.add(ember);
      embers.set(id, ember);
    } else if (st !== 'red' && existing) {
      entry.group.remove(existing);
      existing.geometry.dispose();
      (existing.material as THREE.Material).dispose();
      embers.delete(id);
    }
  }

  function setModuleStateQuiet(id: string, st: ModuleDamageState): void {
    if (!moduleGroups.has(id) || dmgStates.get(id) === st) return;
    dmgStates.set(id, st);
    applyModuleVisual(id, st);
  }

  function setModuleState(id: string, st: ModuleDamageState): ModuleHit | null {
    const entry = moduleGroups.get(id);
    if (!entry || dmgStates.get(id) === st) return null;
    setModuleStateQuiet(id, st);
    const hit: ModuleHit = { id, kind: entry.spec.kind, state: st };
    dmgCb?.(hit);
    return hit;
  }

  function restoreStates(states: readonly { id: string; state: ModuleDamageState }[]): void {
    const byId = new Map(states.map((s) => [s.id, s.state]));
    for (const m of spec.modules) setModuleStateQuiet(m.id, byId.get(m.id) ?? 'green');
  }

  function damageAt(worldHit: THREE.Vector3): ModuleHit | null {
    const local = visual.worldToLocal(worldHit.clone());
    // nearest non-destroyed module by spine position (destroyed absorb only
    // if nothing else is close — a dead section keeps eating fire)
    let best: ModuleSpec | null = null;
    let bestD = Infinity;
    for (const m of spec.modules) {
      const d = Math.abs(m.z - local.z) + (dmgStates.get(m.id) === 'destroyed' ? 3.5 : 0);
      if (d < bestD) { bestD = d; best = m; }
    }
    if (!best) return null;
    const cur = dmgStates.get(best.id) ?? 'green';
    const next = DAMAGE_LADDER[Math.min(DAMAGE_LADDER.indexOf(cur) + 1, DAMAGE_LADDER.length - 1)]!;
    if (next === cur) return { id: best.id, kind: best.kind, state: cur };
    return setModuleState(best.id, next);
  }

  const damage: Custos['damage'] = {
    states: () => spec.modules.map((m) => ({ id: m.id, kind: m.kind, state: dmgStates.get(m.id) ?? 'green' })),
    damageAt,
    setState: setModuleState,
    reset: () => { for (const m of spec.modules) setModuleState(m.id, 'green'); },
    restoreStates,
    driveFactor: () => {
      const drive = spec.modules.find((m) => m.kind === 'drive');
      if (!drive) return 1;
      return { green: 1, yellow: 0.7, red: 0.35, destroyed: 0.05 }[dmgStates.get(drive.id) ?? 'green'];
    },
    onChange: (cb) => { dmgCb = cb; },
  };

  function update(dt: number, t: number): void {
    if (state.deployFraction !== state.deployTarget) {
      const step = dt / POSTURE.travelSeconds;
      // The last thousandth snaps: a hull left a hair open sheds, and so flies,
      // off what the heat ladder plans the closed or open posture at.
      state.deployFraction = Math.abs(state.deployFraction - state.deployTarget) <= 1e-3
        ? state.deployTarget
        : state.deployFraction < state.deployTarget
          ? Math.min(state.deployTarget, state.deployFraction + step)
          : Math.max(state.deployTarget, state.deployFraction - step);
      state.beam = rig.setDeploy(state.deployFraction);
    }
    if (wingSpan) bindWingSpan();
    radiator.loopK = loopTemperature(heatFrac(heat));
    radiator.panelK = stepPanelTemperature(radiator.panelK, dt, state.deployFraction, radiator.loopK);
    for (const g of radGlows) g.update(radiator.panelK, t, state.deployFraction);
    if (!radSeeded) seedRadiators();
    navLights?.update(t);
    const len = THREE.MathUtils.lerp(0.001, 3.0, state.throttle) * (0.9 + 0.1 * Math.sin(t * 30));
    for (const plume of plumes) {
      plume.scale.set(1, len, 1);
      (plume.material as THREE.MeshBasicMaterial).opacity = state.throttle * 0.5;
    }
    driveHeat?.update(state.throttle, dt);
    visual.position.y = Math.sin(t * 0.4) * 0.06;
    hullDamage?.update(t);
    // wounded modules smolder — irregular ember flicker
    let ei = 0;
    for (const ember of embers.values()) {
      const f = 0.55 + 0.45 * Math.sin(t * 11 + ei * 2.7) * Math.sin(t * 3.1 + ei);
      (ember.material as THREE.MeshBasicMaterial).opacity = 0.35 + 0.6 * Math.abs(f);
      ember.scale.setScalar(0.7 + 0.5 * Math.abs(f));
      ei++;
    }
  }

  // ---- dispose: free per-instance geometries + materials. Shared cached
  // textures are intentionally left alive for other ships. ----
  function dispose(): void {
    disposed = true;
    const geos = new Set<THREE.BufferGeometry>();
    const matSet = new Set<THREE.Material>();
    // the shared rad material may be on no mesh once every panel has its own seed
    matSet.add(mats.rad);
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      // kitbash part geometry is shared fleet-wide (parts.ts) — never free it here
      if (mesh.geometry && !mesh.userData.sharedGeometry) geos.add(mesh.geometry);
      const mm = mesh.material;
      if (Array.isArray(mm)) mm.forEach((x) => matSet.add(x)); else if (mm) matSet.add(mm);
    });
    geos.forEach((g) => g.dispose());
    matSet.forEach((m) => m.dispose());
    disposables.forEach((d) => d.dispose());
    root.clear();
  }

  return { group: root, visual, setDeployTarget, restoreDeploy, toggleDeploy, setThrottle, state, heat, radiator, driveHeat, update, dispose, damage };
}
